import React, { useEffect, useState } from 'react';
import { 
  Button, Box, Container, Paper, Typography, Chip, CircularProgress, Backdrop,
  TextField, Grid, InputAdornment, IconButton, Tooltip 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import * as deliveryNoteService from '../services/deliveryNoteService';
import { generateAndPreviewDeliveryNotePDF } from '../services/deliveryNotePdfService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import DescriptionIcon from '@mui/icons-material/Description';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";

const ListDeliveryNotes = () => {
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeliveryNotes();
  }, []);

  const fetchDeliveryNotes = async () => {
    setLoading(true);
    try {
      const response = await deliveryNoteService.getDeliveryNotes();
      const deliveryNotesArray = response?.payload || response?.data || [];
      setDeliveryNotes(deliveryNotesArray);
    } catch (error) {
      console.error('Error fetching delivery notes:', error);
      toast.error(t('failed_to_load_delivery_notes') || 'Failed to load delivery notes');
      setDeliveryNotes([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchDeliveryNotes();
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete_delivery_note') || 'Are you sure you want to delete this delivery note?')) {
      try {
        await deliveryNoteService.deleteDeliveryNote(id);
        toast.success(t('delivery_note_deleted') || 'Delivery note deleted successfully');
        fetchDeliveryNotes();
      } catch (error) {
        console.error('Error deleting delivery note:', error);
        toast.error(t('failed_to_delete_delivery_note') || 'Failed to delete delivery note');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const handlePreviewDeliveryNotePdf = async (deliveryNote) => {
    setPreviewLoading(true);
    try {
      const fullDN = await deliveryNoteService.getDeliveryNoteById(deliveryNote.Id);
      const data = fullDN?.payload || fullDN?.data || fullDN || deliveryNote;
      await generateAndPreviewDeliveryNotePDF(data, storeInfo, t);
    } catch (error) {
      console.error('Error generating delivery note PDF:', error);
      toast.error(t('failed_to_generate_pdf') || 'Failed to generate PDF');
    } finally {
      setPreviewLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusColors = {
      'Pending': 'warning',
      'Delivered': 'success',
      'Cancelled': 'error',
    };
    
    return (
      <Chip
        label={status || 'Pending'}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  // Filter delivery notes based on search query
  const filteredDeliveryNotes = deliveryNotes.filter((dn) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (dn.DeliveryNoteNumber?.toLowerCase().includes(query)) ||
      (dn.CustomerName?.toLowerCase().includes(query)) ||
      (dn.Status?.toLowerCase().includes(query))
    );
  });

  const columns = [
    {
      field: 'DeliveryNoteNumber',
      headerName: t('delivery_note_number') || 'Delivery Note #',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'CustomerName',
      headerName: t('customer') || 'Customer',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'IssueDate',
      headerName: t('date') || 'Date',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDate(params.row.IssueDate || params.row.DeliveryNoteDate || params.row.CreatedDate || ''),
    },
    {
      field: 'Status',
      headerName: t('status') || 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'TotalItems',
      headerName: t('items') || 'Items',
      flex: 0.5,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'TotalValue',
      headerName: t('total') || 'Total',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatCurrency(params.row.TotalValue || params.row.TotalAmount || params.row.GrandTotal || 0),
    },
    {
      field: 'PDF',
      headerName: 'PDF',
      flex: 0.4,
      minWidth: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) =>
        params.row ? (
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              '&:hover': { transform: 'scale(1.1)', transition: 'transform 0.2s' }
            }}
            title="Preview PDF"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewDeliveryNotePdf(params.row);
            }}
          >
            <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
          </Box>
        ) : null,
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Actions',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('view') || 'View'}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/sales/delivery-notes/view/${params.row.Id}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('edit') || 'Edit'}>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => navigate(`/sales/delivery-notes/edit/${params.row.Id}`)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('delete') || 'Delete'}>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.Id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000, backgroundColor: 'rgba(0,0,0,0.7)' }}
        open={previewLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('generating_pdf') || 'Generating PDF...'}
          </Typography>
        </Box>
      </Backdrop>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DescriptionIcon sx={{ fontSize: 40, color: 'white' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                  {t('delivery_notes') || 'Delivery Notes'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {t('manage_delivery_notes') || 'Manage your delivery notes'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate('/sales/delivery-notes/add')}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                borderRadius: 2,
                px: 3,
              }}
            >
              {t('create_delivery_note') || 'Create Delivery Note'}
            </Button>
          </Box>
        </Paper>

        {/* Filters */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('search_delivery_notes') || 'Search by delivery note number, customer...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={t('refresh') || 'Refresh'}>
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Grid */}
        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ReusableDataGrid
              rows={filteredDeliveryNotes}
              columns={columns}
              loading={loading}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              getRowId={(row) => row.Id}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ListDeliveryNotes;
